/**
 * /funny — The Funny Page.
 *
 * Featured comic with reactions, comments, + recent strip of previous comics.
 * Follows the same layout pattern as TracksPage.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext.jsx';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { getComicsHome, getComicReactions, reactToComic } from '../api/comics.js';
import SiteHeader from '../components/SiteHeader.jsx';
import ComicViewer from '../components/comics/ComicViewer.jsx';
import ReactionBar from '../components/tracks/ReactionBar.jsx';
import CommentsSection from '../components/tracks/CommentsSection.jsx';
import './funny.css';


export default function FunnyPage() {
  usePageTitle('The Funny Page');

  const { user } = useAuth();
  const isAuth = Boolean(user);

  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reactionCounts, setReactionCounts] = useState({});
  const [myReactions, setMyReactions] = useState([]);

  const loadEverything = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getComicsHome();
      setHome(data);

      if (data.current) {
        const reacts = await getComicReactions(data.current.id);
        setReactionCounts(reacts.counts || {});
        setMyReactions(reacts.my_reactions || []);
      } else {
        setReactionCounts({});
        setMyReactions([]);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEverything();
  }, [loadEverything, isAuth]);

  async function handleReact(emoji) {
    if (!home?.current) return;
    const result = await reactToComic(home.current.id, emoji);
    setReactionCounts(result.counts);
    setMyReactions((prev) =>
      result.active ? [...prev, emoji] : prev.filter((e) => e !== emoji)
    );
  }

  const { current, recent } = home || {};

  return (
    <div className="funny-page">
      <SiteHeader adminHref="/admin/comics" />

      {/* ── page identity ── */}
      <header className="funny-header">
        <h1 className="funny-title">the funny page</h1>
        <p className="funny-subtitle">
          family humor. viewer discretion is not advised.
        </p>
      </header>

      {loading && (
        <div className="funny-loading">loading the goods...</div>
      )}

      {error && (
        <div className="funny-error">
          something broke. text Tim.
        </div>
      )}

      {/* ── featured comic ── */}
      {current && (
        <section className="funny-hero">
          <div className="funny-hero__comic">
            <ComicViewer
              src={current.file_path}
              alt={current.title}
            />
          </div>

          <div className="funny-hero__meta">
            <h2 className="funny-hero__title">{current.title}</h2>
            {current.description && (
              <p className="funny-hero__desc">{current.description}</p>
            )}
          </div>

          <div className="funny-hero__reactions">
            <ReactionBar
              counts={reactionCounts}
              myReactions={myReactions}
              onToggle={handleReact}
              isAuthenticated={isAuth}
            />
          </div>

          <div className="funny-hero__comments">
            <CommentsSection
              targetType="comic"
              targetId={current.id}
            />
          </div>
        </section>
      )}

      {!loading && !current && !error && (
        <section className="funny-empty">
          <div className="funny-empty__icon">🐔</div>
          <div className="funny-empty__text">
            no comics yet. the chicken is still workshopping material.
          </div>
        </section>
      )}

      {/* ── recent strip ── */}
      {recent && recent.length > 0 && (
        <section className="funny-recent">
          <header className="funny-section-head">
            <span className="funny-section-label">previous laughs</span>
          </header>
          <div className="funny-recent__strip">
            {recent.map((c) => (
              <Link
                key={c.id}
                to={`/funny/${c.id}`}
                className="funny-tile"
              >
                <div className="funny-tile__thumb">
                  <img
                    src={c.thumb_path || c.file_path}
                    alt={c.title}
                    loading="lazy"
                  />
                </div>
                <div className="funny-tile__title">{c.title}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
